import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { taskService } from '../api/taskService';
import { userService } from '../api/userService';
import TaskForm from '../components/TaskForm';
import DGFilterBar from '../components/DGFilterBar';
import { useAuth } from '../auth/AuthContext';
import { useLocation } from 'react-router-dom';
import { formatDueDate, isDueUpcoming, formatDate, getDaysDifference, formatDateTimeIST } from '../utils/dateUtils';
import { getUserName } from '../utils/userUtils';
import { Layout, Plus, X, ChevronDown, ChevronRight, ShieldCheck, Circle, MessageCircle, Paperclip, Upload, Eye, Download, Trash2 } from 'lucide-react';
import ContentLoader from '../components/ContentLoader';
import UserSearchSelect from '../components/UserSearchSelect';

const Tasks = () => {
    const { user } = useAuth();
    const location = useLocation();
    const isDG = user?.roles?.some(r => r === 'DG' || r === 'DG_OFFICE');
    const isCoordinator = user?.roles?.some(r => r.startsWith('COORDINATOR_') || r === 'COORDINATOR_RIS');
    const isEmployee = !isDG && !isCoordinator;

    const [activeFilter, setActiveFilter] = useState('All'); // New Filter State
    const [activeSubFilter, setActiveSubFilter] = useState(null); // Sub-filter State

    const [allTasks, setAllTasks] = useState([]); // Store all fetched tasks
    const [tasks, setTasks] = useState([]); // Store filtered tasks
    const [users, setUsers] = useState([]); // Store users for search resolution
    const [filters, setFilters] = useState({});

    const [isCreating, setIsCreating] = useState(false);
    const [loading, setLoading] = useState(true);

    // Context-Aware Navigation
    const searchParams = new URLSearchParams(location.search);
    const divisionParam = searchParams.get('division');
    // If DG and division param exists, use it. Otherwise fallback to user's division.
    // Enhanced Logic: Coordinators should also see division name
    const getActiveDivisionName = () => {
        if (divisionParam) return divisionParam;
        if (isDG) return 'All';
        if (isCoordinator) return user?.attributes?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_').slice(1).join('_') || 'My Division';
        return user?.division || 'My';
    };
    const activeDivision = getActiveDivisionName();

    // Title mapping
    const displayDivisionName = (activeDivision === 'CORE' || activeDivision === 'RIS') ? 'RIS' : (activeDivision || '').replace('_', ' ');

    // Task Types for Filtering
    const TASK_TYPES = {
        "Publication": ["Journals", "Books/Report", "Discussion Papers", "Occasional Papers", "Policy Briefs", "Blogs", "Newsletters", "RIS Diary", "RIS Annual Report"],
        "Research": ["Data Analysis", "Field Survey", "Literature Review", "Report Writing"],
        "Governance": ["Committee Meeting", "Board Review", "Compliance"],
        "Admin": ["HR", "Finance", "IT Support"],
        "Pre Event": ["Venue Booking", "Agenda Setting", "Speaker Confirmation", "MEA Approval", "List of Speakers", "Concept Note", "Concept Note Approval", "Concept Note Finalization", "Concept Note Distribution"],
        "Post Event": ["Transcript Upload", "Report Writing", "Follow-up"]
    };

    const [error, setError] = useState(null);

    // Subtask State
    const [expandedTaskId, setExpandedTaskId] = useState(null);
    const [subtasks, setSubtasks] = useState({}); // Map taskId -> subtaskList
    const [creatingSubtaskFor, setCreatingSubtaskFor] = useState(null);
    const [newSubtaskData, setNewSubtaskData] = useState({ activityType: '', activityNameManual: '', completionDate: '', assigneeId: '', assigneeName: '', skills: [] });
    const [subtaskStatusMenuOpen, setSubtaskStatusMenuOpen] = useState(null); // Track which subtask's menu is open
    const [activeCommentModal, setActiveCommentModal] = useState(null); // Tracks which subtask has the comment popover open
    const [subtaskComments, setSubtaskComments] = useState({});

    // Parent Task Comment states
    const [activeTaskCommentModal, setActiveTaskCommentModal] = useState(null);
    const [taskComments, setTaskComments] = useState({});
    const [uploadingByTask, setUploadingByTask] = useState({});
    const [uploadCategoryByTask, setUploadCategoryByTask] = useState({});
    const fileInputRefs = useRef({});
    const hydratedTaskIdsRef = useRef(new Set());
    const MAX_FILE_BYTES = 100 * 1024 * 1024;
    const ALLOWED_EXTENSIONS = ['pdf', 'txt', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'csv'];
    const TASK_ATTACHMENT_CATEGORIES = [
        { value: 'PUBLICATION', label: 'Publication (General)' },
        { value: 'PUBLICATION_JOURNALS', label: 'Publication - Journals' },
        { value: 'PUBLICATION_BOOKS_REPORT', label: 'Publication - Books/Report' },
        { value: 'PUBLICATION_DISCUSSION_PAPERS', label: 'Publication - Discussion Papers' },
        { value: 'PUBLICATION_OCCASIONAL_PAPERS', label: 'Publication - Occasional Papers' },
        { value: 'PUBLICATION_POLICY_BRIEFS', label: 'Publication - Policy Briefs' },
        { value: 'PUBLICATION_BLOGS', label: 'Publication - Blogs' },
        { value: 'PUBLICATION_NEWSLETTERS', label: 'Publication - Newsletters' },
        { value: 'PUBLICATION_RIS_DIARY', label: 'Publication - RIS Diary' },
        { value: 'PUBLICATION_RIS_ANNUAL_REPORT', label: 'Publication - RIS Annual Report' },
        { value: 'RESEARCH', label: 'Research (General)' },
        { value: 'RESEARCH_DATA_ANALYSIS', label: 'Research - Data Analysis' },
        { value: 'RESEARCH_FIELD_SURVEY', label: 'Research - Field Survey' },
        { value: 'RESEARCH_LITERATURE_REVIEW', label: 'Research - Literature Review' },
        { value: 'RESEARCH_REPORT_WRITING', label: 'Research - Report Writing' },
        { value: 'DRAFT', label: 'Draft' },
        { value: 'IT', label: 'IT' },
        { value: 'ADMIN', label: 'Admin' },
        { value: 'OTHER', label: 'Other' }
    ];
    const getLatestTimestamp = (item) => {
        const candidates = [item?.CreatedAt, item?.UpdatedAt, item?.StartDate, item?.EndDate, item?.DueDate];
        for (const value of candidates) {
            const ts = value ? new Date(value).getTime() : NaN;
            if (!Number.isNaN(ts)) return ts;
        }
        return 0;
    };

    const queryClient = useQueryClient();

    const { data: usersData = [], isLoading: loadingUsers } = useQuery({
        queryKey: ['users'],
        queryFn: () => userService.getAllUsers().catch(() => []),
        staleTime: 300000
    });

    const usersList = useMemo(() => Array.isArray(usersData) ? usersData : (usersData?.Users || []), [usersData]);

    const { data: allTasksData = [], isLoading: loadingTasks, error: tasksErrorObj } = useQuery({
        queryKey: ['tasks', divisionParam, activeDivision, isDG, isCoordinator],
        queryFn: async () => {
            if (divisionParam) return await taskService.getDivisionTasks(divisionParam);
            if (isCoordinator) {
                const userDivision = user?.attributes?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_').slice(1).join('_');
                return userDivision ? await taskService.getDivisionTasks(userDivision) : await taskService.getAll();
            }
            if (isDG) return await taskService.getAllTasks();
            return await taskService.getAll();
        },
        staleTime: 60000,
        enabled: !!user
    });

    useEffect(() => {
        setAllTasks(allTasksData || []);
    }, [allTasksData]);

    useEffect(() => {
        setUsers(usersList);
    }, [usersList]);

    useEffect(() => {
        setLoading(loadingTasks || loadingUsers);
    }, [loadingTasks, loadingUsers]);

    useEffect(() => {
        if (tasksErrorObj) setError(tasksErrorObj.message);
    }, [tasksErrorObj]);

    // Hydrate missing subtask counters so progress is stable before expand/collapse.
    useEffect(() => {
        const candidates = allTasks.filter(t =>
            t?.TaskID &&
            !hydratedTaskIdsRef.current.has(t.TaskID) &&
            (typeof t.TotalSubTasks !== 'number' || typeof t.CompletedSubTasks !== 'number')
        );

        if (candidates.length === 0) return;
        let cancelled = false;

        const hydrate = async () => {
            const counterUpdates = {};
            const subtasksUpdates = {};

            await Promise.all(candidates.map(async (task) => {
                hydratedTaskIdsRef.current.add(task.TaskID);
                try {
                    let subs = [];
                    if (Array.isArray(task.Subtasks)) {
                        subs = task.Subtasks;
                    } else {
                        subs = await taskService.getSubTasks(task.TaskID);
                    }

                    const total = subs.length;
                    const completed = subs.filter(s => s.Status === 'Completed').length;
                    counterUpdates[task.TaskID] = { TotalSubTasks: total, CompletedSubTasks: completed };
                    subtasksUpdates[task.TaskID] = subs;
                } catch (e) {
                    console.warn(`Failed to hydrate subtasks for task ${task.TaskID}`, e);
                }
            }));

            if (cancelled || Object.keys(counterUpdates).length === 0) return;

            setSubtasks(prev => ({ ...prev, ...subtasksUpdates }));
            setAllTasks(prev => prev.map(t => counterUpdates[t.TaskID] ? { ...t, ...counterUpdates[t.TaskID] } : t));
        };

        hydrate();
        return () => { cancelled = true; };
    }, [allTasks]);

    // Filter Logic
    useEffect(() => {
        let filtered = [...allTasks];
        const searchParams = new URLSearchParams(location.search);
        const urlFilter = searchParams.get('filter');
        const urlTaskId = searchParams.get('taskId');

        // 0. URL Filtering (Dashboard Links & Event Links)
        if (urlTaskId) {
            filtered = filtered.filter(t => t.TaskID === urlTaskId);
        } else if (urlFilter) {
            const now = new Date();
            const getDiff = (d) => {
                if (!d) return Infinity;
                const date = new Date(d);
                return Math.ceil(Math.abs(now - date) / (1000 * 60 * 60 * 24));
            };

            const strFilter = (urlFilter || '').toString().toLowerCase();

            if (strFilter === 'completed_7d') {
                filtered = filtered.filter(t => t.Status === 'Completed' && getDiff(t.UpdatedAt) <= 7);
            } else if (strFilter === 'updated_7d') {
                filtered = filtered.filter(t => getDiff(t.UpdatedAt) <= 7);
            } else if (strFilter === 'created_7d') {
                filtered = filtered.filter(t => getDiff(t.CreatedAt) <= 7);
            } else if (strFilter.startsWith('due_')) {
                const days = parseInt(strFilter.split('_')[1]);
                if (!isNaN(days)) {
                    filtered = filtered.filter(t => isDueUpcoming(t.EndDate || t.DueDate, days));
                }
            } else if (strFilter === 'active') {
                filtered = filtered.filter(t => t.Status !== 'Completed' && t.Status !== 'Cancelled');
            } else if (strFilter === 'overdue') {
                filtered = filtered.filter(t => t.Status !== 'Completed' && t.Status !== 'Cancelled' && (t.DueDate || t.EndDate) && new Date(t.DueDate || t.EndDate) < now);
            } else if (strFilter === 'pending') {
                filtered = filtered.filter(t => t.Status === 'Pending' || t.Status === 'Pending Review');
            }
        }

        // 1. Tab Filtering (Task Type) - ONLY if no URL override is active
        if (!urlFilter && !urlTaskId) {
            if (activeFilter !== 'All') {
                filtered = filtered.filter(t => t.TaskType === activeFilter);
            }

            // 1.1 Sub-Type Filtering
            if (activeSubFilter) {
                filtered = filtered.filter(t =>
                    t.SubTaskType === activeSubFilter ||
                    (t.SubTaskType && t.SubTaskType.toLowerCase() === activeSubFilter.toLowerCase())
                );
            }
        }

        // 2. Global Filter Bar (Search, Date, Status, InvolvedOnly)
        if (filters) {
            const { searchTerm, startDate, endDate, status, involvedOnly } = filters;

            // Date Filtering
            if (startDate) {
                const sDate = new Date(startDate);
                sDate.setHours(0, 0, 0, 0);
                filtered = filtered.filter(t => new Date(t.StartDate || t.CreatedAt || t.UpdatedAt || Date.now()) >= sDate);
            }
            if (endDate) {
                const eDate = new Date(endDate);
                eDate.setHours(23, 59, 59, 999);
                filtered = filtered.filter(t => new Date(t.StartDate || t.CreatedAt || t.UpdatedAt || Date.now()) <= eDate);
            }

            // Status Filtering
            if (status && status !== '' && status !== 'All') {
                filtered = filtered.filter(t => t.Status === status);
            }

            // Involved Only Filtering
            if (involvedOnly) {
                const myId = String(user?.sub || user?.Username || user?.UserId || user?.id || '');
                const myEmail = String(user?.Email || user?.Attributes?.find(a => a.Name === 'email')?.Value || '');
                const myName = String(user?.Name || user?.Attributes?.find(a => a.Name === 'name')?.Value || '');

                filtered = filtered.filter(t => {
                    const tCreator = String(t.CreatedBy || '');
                    const tCreatorId = String(t.CreatedByID || '');
                    const isCreator = (tCreatorId && tCreatorId === myId) ||
                        (tCreator && (tCreator === myName || tCreator === myEmail || tCreator === myId));

                    const assigneeId = String(t.AssigneeID || t.AssignedTo || '');
                    const assigneeName = String(t.AssigneeName || '');

                    const isAssignee = (assigneeId && (assigneeId === myId || assigneeId === myEmail || assigneeId === myName)) ||
                        (assigneeName && myName && assigneeName === myName);

                    const isSubtaskAssignee = Array.isArray(t.Subtasks) && t.Subtasks.some(sub => {
                        const subAssigneeId = String(sub.AssigneeID || '');
                        const subAssigneeName = String(sub.AssigneeName || '');
                        return (subAssigneeId && (subAssigneeId === myId || subAssigneeId === myEmail || subAssigneeId === myName)) ||
                            (subAssigneeName && myName && subAssigneeName === myName);
                    });

                    return isCreator || isAssignee || isSubtaskAssignee;
                });
            }

            // Search Filtering
            if (searchTerm && searchTerm.trim() !== '') {
                const term = searchTerm.toLowerCase();

                // Find IDs of users matching the search term (Name, Email, Division)
                const matchingUserIds = new Set(users.filter(u =>
                    (u.Name && u.Name.toLowerCase().includes(term)) ||
                    (u.Email && u.Email.toLowerCase().includes(term)) ||
                    (u.Division && u.Division.toLowerCase().includes(term))
                ).map(u => String(u.UserId || u.Username || u.id)));

                filtered = filtered.filter(t =>
                    (t.Title && String(t.Title).toLowerCase().includes(term)) ||
                    (t.Division && String(t.Division).toLowerCase().includes(term)) ||
                    (t.AssigneeDivision && String(t.AssigneeDivision).toLowerCase().includes(term)) ||
                    (t.AssigneeID && matchingUserIds.has(String(t.AssigneeID))) ||
                    (t.CreatedBy && matchingUserIds.has(String(t.CreatedBy))) ||
                    (t.CreatedBy && String(t.CreatedBy).toLowerCase().includes(term)) ||
                    (t.AssigneeName && String(t.AssigneeName).toLowerCase().includes(term))
                );
            }
        }

        filtered.sort((a, b) => getLatestTimestamp(b) - getLatestTimestamp(a));

        // Auto-transition 'New' tasks to 'Pending' if they are overdue
        filtered = filtered.map(t => {
            if (t.Status === 'New') {
                const diff = getDaysDifference(t.EndDate || t.DueDate);
                if (diff !== null && diff < 0) {
                    const isFullyCompleted = typeof t.TotalSubTasks === 'number' && t.TotalSubTasks > 0 && t.CompletedSubTasks === t.TotalSubTasks;
                    if (isFullyCompleted) {
                        return { ...t, Status: 'In Progress' };
                    }
                    return { ...t, Status: 'Pending' };
                }
            }
            return t;
        });

        setTasks(filtered);
    }, [filters, allTasks, isDG, users, activeFilter, activeSubFilter, location.search, user]);


    const [initialFormState, setInitialFormState] = useState(null);

    // Categories for Tabs
    const filterTabs = ["All", "Publication", "Research", "Governance", "Admin", "Pre Event", "Post Event"];

    const handleTabChange = (tab) => {
        setActiveFilter(tab);
        setActiveSubFilter(null); // Reset sub-filter when switching main tabs
    };

    const handleCreate = async (taskData) => {
        try {
            const { attachmentsFiles = [], attachmentsCategory = 'PUBLICATION', predefinedSubtasks = [], ...baseTaskData } = taskData || {};
            // 1. Infer Assigee (Self if empty)
            const assigneeId = baseTaskData.assigneeId || user.sub || user.username;

            // 2. Infer Cross-Division
            let isCrossDivision = false;
            let assigneeDivision = null;

            // Find assignee details
            const assigneeUser = users.find(u => (u.sub || u.Username || u.UserId) === assigneeId);
            if (assigneeUser) {
                assigneeDivision = assigneeUser.Division || assigneeUser.Attributes?.find(a => a.Name === 'custom:division')?.Value;

                // Compare with creator division - PRIORITY: Enriched attributes > Direct property > Role extraction
                let myDivision = user.attributes?.division || user.division || user.attributes?.['custom:division'];

                if (!myDivision && isCoordinator) {
                    const coordGroup = user.roles?.find(r => r.startsWith('COORDINATOR_'));
                    if (coordGroup) {
                        myDivision = coordGroup.split('_')[1];
                    }
                }

                // If divisions exist and differ, flag it
                if (myDivision && assigneeDivision && myDivision !== assigneeDivision) {
                    isCrossDivision = true;
                }
            }

            const payload = {
                ...baseTaskData,
                assigneeId,
                isCrossDivision,
                status: 'New' // Force New on creation
            };

            const created = await taskService.create(payload);
            const createdTaskId = created?.taskId;
            if (createdTaskId && Array.isArray(attachmentsFiles) && attachmentsFiles.length > 0) {
                console.log(`[Tasks] Starting upload for ${attachmentsFiles.length} files to task ${createdTaskId}`);
                await uploadAttachmentsForTask(createdTaskId, attachmentsFiles, attachmentsCategory, true);
                console.log(`[Tasks] Upload sequence completed`);
            }

            // Automatically Generate Pre/Post Event Subtasks
            if (createdTaskId && predefinedSubtasks.length > 0) {
                await Promise.all(predefinedSubtasks.map(async sub => {
                    const subAssigneeUser = users.find(u => (u.sub || u.Username || u.UserId) === sub.assigneeId);

                    const subtaskPayload = {
                        activityType: sub.name,
                        activityNameManual: sub.name,
                        completionDate: sub.endDate || baseTaskData.endDate || '',
                        assigneeId: sub.assigneeId || '',
                        assigneeName: subAssigneeUser ? (subAssigneeUser.Name || subAssigneeUser.name || subAssigneeUser.Username) : '',
                        skills: []
                    };

                    return taskService.createSubTask(createdTaskId, subtaskPayload).catch(e => {
                        console.error(`Failed to create subtask ${sub.name}:`, e);
                    });
                }));
            }

            setIsCreating(false);
            setInitialFormState(null); // Reset
            queryClient.invalidateQueries({ queryKey: ['tasks'] }); // Refresh list
        } catch (err) {
            console.error(err);
            alert("Failed to create task");
        }
    };

    const upsertTaskAttachment = (taskId, attachment) => {
        const merge = (collection) => collection.map(t => {
            if (t.TaskID !== taskId) return t;
            const current = Array.isArray(t.Attachments) ? t.Attachments : [];
            const next = [...current.filter(a => a.AttachmentID !== attachment.AttachmentID), attachment];
            return { ...t, Attachments: next };
        });
        setTasks(prev => merge(prev));
        setAllTasks(prev => merge(prev));
    };

    const validateFiles = (files) => {
        const valid = [];
        const invalid = [];
        for (const file of Array.from(files || [])) {
            const ext = ((file?.name || '').split('.').pop() || '').toLowerCase();
            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                invalid.push(`${file.name}: unsupported file type`);
                continue;
            }
            if ((file?.size || 0) > MAX_FILE_BYTES) {
                invalid.push(`${file.name}: exceeds 100MB`);
                continue;
            }
            valid.push(file);
        }
        return { valid, invalid };
    };

    const uploadAttachmentsForTask = async (taskId, files, category = 'PUBLICATION', silent = false) => {
        const { valid: list, invalid } = validateFiles(files);
        if (invalid.length > 0 && !silent) {
            alert(`Some files were rejected:\n${invalid.join('\n')}`);
        }
        if (list.length === 0) return;

        setUploadingByTask(prev => ({ ...prev, [taskId]: { total: list.length, done: 0, active: true } }));

        for (const file of list) {
            try {
                const prep = await taskService.prepareAttachment(taskId, file, category);
                const uploadUrl = prep?.uploadUrl;
                const requiredHeaders = prep?.requiredHeaders || {};
                const attachmentMeta = prep?.attachment;
                if (!uploadUrl || !attachmentMeta) throw new Error('Upload URL generation failed');

                const putResp = await fetch(uploadUrl, {
                    method: 'PUT',
                    headers: requiredHeaders,
                    body: file
                });
                if (!putResp.ok) throw new Error(`S3 upload failed (${putResp.status})`);

                const committed = await taskService.commitAttachment(taskId, {
                    ...attachmentMeta,
                    fileName: file.name,
                    contentType: file.type || attachmentMeta.contentType || 'application/octet-stream',
                    sizeBytes: file.size,
                    category
                });
                if (committed?.attachment) {
                    upsertTaskAttachment(taskId, committed.attachment);
                }
            } catch (err) {
                console.error('Task attachment upload failed', err);
                if (!silent) alert(`Failed to upload "${file.name}": ${err.message}`);
            } finally {
                setUploadingByTask(prev => {
                    const current = prev[taskId] || { total: list.length, done: 0, active: true };
                    const done = Math.min(current.done + 1, current.total);
                    return { ...prev, [taskId]: { total: current.total, done, active: done < current.total } };
                });
            }
        }
    };

    const handleTaskAttachmentPicker = (taskId) => {
        const input = fileInputRefs.current[taskId];
        if (input) input.click();
    };

    const handleTaskAttachmentSelection = (taskId, inputEvent) => {
        const files = Array.from(inputEvent?.target?.files || []);
        if (files.length === 0) return;
        const category = uploadCategoryByTask[taskId] || 'PUBLICATION';
        uploadAttachmentsForTask(taskId, files, category, false);
        inputEvent.target.value = '';
    };

    const openTaskAttachment = async (taskId, attachmentId, isDownload = false) => {
        try {
            const data = await taskService.getAttachmentUrl(taskId, attachmentId, { download: isDownload });
            if (data?.url) window.open(data.url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            alert('Unable to open attachment: ' + err.message);
        }
    };

    const deleteTaskAttachment = async (taskId, attachmentId) => {
        if (!window.confirm('Delete this attachment?')) return;
        try {
            await taskService.deleteAttachment(taskId, attachmentId);
            const remove = (collection) => collection.map(t => {
                if (t.TaskID !== taskId) return t;
                const current = Array.isArray(t.Attachments) ? t.Attachments : [];
                return { ...t, Attachments: current.filter(a => a.AttachmentID !== attachmentId) };
            });
            setTasks(prev => remove(prev));
            setAllTasks(prev => remove(prev));
        } catch (err) {
            alert('Failed to delete attachment: ' + err.message);
        }
    };

    const toggleExpand = async (taskId) => {
        if (expandedTaskId === taskId) {
            setExpandedTaskId(null);
            return;
        }

        setExpandedTaskId(taskId);

        // Fetch subtasks if not already loaded
        if (!subtasks[taskId]) {
            try {
                const subs = await taskService.getSubTasks(taskId);
                setSubtasks(prev => ({ ...prev, [taskId]: subs }));
            } catch (err) {
                console.error("Failed to load subtasks", err);
            }
        }
    };

    const handleOpenSubtaskForm = (taskId) => {
        setCreatingSubtaskFor(taskId);
        if (isEmployee) {
            setNewSubtaskData(prev => ({
                ...prev,
                assigneeId: user?.sub || user?.UserId || user?.Username || '',
                assigneeName: user?.attributes?.name || user?.Name || user?.name || user?.username || ''
            }));
        }
    };

    const handleCreateSubtask = async (taskId) => {
        try {
            await taskService.createSubTask(taskId, newSubtaskData);
            setCreatingSubtaskFor(null);
            setNewSubtaskData({ activityType: '', activityNameManual: '', completionDate: '', assigneeId: '', assigneeName: '', skills: [] });

            // Refresh subtasks
            const subs = await taskService.getSubTasks(taskId);
            setSubtasks(prev => ({ ...prev, [taskId]: subs }));
        } catch (err) {
            alert("Failed to create subtask: " + err.message);
        }
    };

    const handleUpdateTaskStatus = async (taskId, newStatus, e) => {
        if (e) e.stopPropagation();

        const task = tasks.find(t => t.TaskID === taskId);
        if (!task) return;

        // Prevent change if already Completed or Cancelled
        if (task.Status === 'Completed' || task.Status === 'Cancelled') {
            alert("Task Status cannot change now");
            return;
        }

        if (newStatus === 'Completed' || newStatus === 'Cancelled') {
            if (!window.confirm(`Are you sure you want to mark this task as ${newStatus}? This cannot be undone.`)) {
                return;
            }
        }

        try {
            // Only send the status field (backend expects `status` lowercase)
            await taskService.update(taskId, { status: newStatus });

            // Optimistic update
            setTasks(prev => prev.map(t => t.TaskID === taskId ? { ...t, Status: newStatus } : t));
            setAllTasks(prev => prev.map(t => t.TaskID === taskId ? { ...t, Status: newStatus } : t));

            // Parent Completed => all subtasks Completed + parent progress 100%.
            if (newStatus === 'Completed') {
                let subs = subtasks[taskId];
                if (!Array.isArray(subs)) {
                    try {
                        subs = await taskService.getSubTasks(taskId);
                    } catch (fetchErr) {
                        console.warn(`Failed to fetch subtasks for ${taskId}`, fetchErr);
                        subs = [];
                    }
                }

                const completedSubs = (subs || []).map(sub => ({ ...sub, Status: 'Completed' }));
                const total = completedSubs.length;
                const completed = total;

                setSubtasks(prev => ({ ...prev, [taskId]: completedSubs }));
                setTasks(prev => prev.map(t => t.TaskID === taskId ? { ...t, TotalSubTasks: total, CompletedSubTasks: completed, Status: 'Completed' } : t));
                setAllTasks(prev => prev.map(t => t.TaskID === taskId ? { ...t, TotalSubTasks: total, CompletedSubTasks: completed, Status: 'Completed' } : t));
            } else if (newStatus === 'Cancelled' && subtasks[taskId]) {
                setSubtasks(prev => ({
                    ...prev,
                    [taskId]: prev[taskId].map(sub => ({ ...sub, Status: 'Cancelled' }))
                }));
            }
        } catch (err) {
            console.error("Update failed", err);
            const msg = err.response?.data || err.message;
            alert(`Failed to update task status: ${msg}`);
        }
    };

    const handleTransferAction = async (taskId, action) => {
        if (!window.confirm(`Are you sure you want to ${action === 'APPROVE_TRANSFER' ? 'Approve' : 'Reject'} this task assignment?`)) return;
        try {
            const res = await taskService.update(taskId, { action });
            const updater = prev => prev.map(t => {
                if (t.TaskID === taskId) {
                    if (action === 'APPROVE_TRANSFER') {
                        return { ...t, TransferStatus: 'Approved', AssigneeID: res.newAssigneeId || t.PendingTargetAssigneeId };
                    } else {
                        return { ...t, TransferStatus: 'Rejected', AssigneeID: res.newAssigneeId || t.CreatedBy, Status: 'Cancelled' };
                    }
                }
                return t;
            });
            setTasks(updater);
            setAllTasks(updater);
        } catch (err) {
            alert('Failed to process transfer: ' + (err.response?.data?.error?.message || err.message));
        }
    };

    const handleSubtaskStatus = async (taskId, subTaskId, newStatus) => {
        // Confirmation for Cancelled
        if (newStatus === 'Cancelled') {
            if (!window.confirm("Are you sure you want to cancel this subtask?")) {
                return;
            }
        }

        try {
            await taskService.updateSubTask(subTaskId, newStatus);
            // Refresh subtasks
            const subs = await taskService.getSubTasks(taskId);
            setSubtasks(prev => ({ ...prev, [taskId]: subs }));

            // AUTO-UPDATE PARENT PROGRESS & STATUS
            // Calculate new stats
            const total = subs.length;
            const completed = subs.filter(s => s.Status === 'Completed').length;

            // Update parent in local state to reflect progress immediately
            setTasks(prev => prev.map(t => {
                if (t.TaskID === taskId) {
                    return { ...t, CompletedSubTasks: completed, TotalSubTasks: total };
                }
                return t;
            }));
            setAllTasks(prev => prev.map(t => {
                if (t.TaskID === taskId) {
                    return { ...t, CompletedSubTasks: completed, TotalSubTasks: total };
                }
                return t;
            }));

            // If any subtask is Completed/In Progress, Parent should be 'In Progress' (unless already Completed)
            const parentTask = tasks.find(t => t.TaskID === taskId);
            // Also check if ALL subtasks are completed? User didn't ask for auto-complete parent, but auto-in-progress is good.
            if (parentTask && parentTask.Status === 'Pending' && (newStatus === 'Completed' || newStatus === 'In Progress')) {
                await handleUpdateTaskStatus(taskId, 'In Progress', { stopPropagation: () => { } });
            }
        } catch (err) {
            const msg = err.response?.data || err.message;
            alert("Failed to update subtask status: " + msg);
        }
    };

    const handleAddSubtaskComment = async (taskId, subTaskId, currentStatus) => {
        const text = subtaskComments[subTaskId] || '';
        const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
        if (wordCount === 0 || wordCount > 20) return;

        try {
            await taskService.updateSubTask(subTaskId, {
                status: currentStatus,
                comment: {
                    text: text.trim(),
                    userName: user?.Name || user?.name || user?.Username || 'Unknown'
                }
            });
            // Clear input
            setSubtaskComments(prev => ({ ...prev, [subTaskId]: '' }));
            // Refresh subtasks
            const subs = await taskService.getSubTasks(taskId);
            setSubtasks(prev => ({ ...prev, [taskId]: subs }));
        } catch (err) {
            alert("Failed to add comment: " + err.message);
        }
    };

    const handleAddParentTaskComment = async (taskId, currentStatus) => {
        const text = taskComments[taskId] || '';
        const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
        if (wordCount === 0 || wordCount > 20) return;

        try {
            await taskService.update(taskId, {
                status: currentStatus,
                comment: {
                    text: text.trim(),
                    userName: user?.Name || user?.name || user?.Username || 'Unknown'
                }
            });

            // Clear input
            setTaskComments(prev => ({ ...prev, [taskId]: '' }));

            // Append locally to prevent full refetch
            const newComment = {
                Text: text.trim(),
                AddedBy: user?.sub || user?.Username || user?.UserId || user?.id || 'Unknown',
                AddedByName: user?.Name || user?.name || user?.Username || 'Unknown',
                Timestamp: new Date().toISOString()
            };

            const appendComment = (prevTasks) => prevTasks.map(t => {
                if (t.TaskID === taskId) {
                    return { ...t, Comments: [...(t.Comments || []), newComment] };
                }
                return t;
            });

            setTasks(appendComment);
            setAllTasks(appendComment);

        } catch (err) {
            alert("Failed to add comment: " + err.message);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ flexShrink: 0 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                        <Layout />
                        {activeFilter === 'All' ? 'Tasks' : `${activeFilter} Tasks`}
                    </h2>
                    {!isCreating && (
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                            {/* Filter Tabs */}
                            <div style={{ display: 'flex', background: '#f8f9fa', padding: '0.25rem', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                                {filterTabs.map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => handleTabChange(tab)}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            background: activeFilter === tab ? 'white' : 'transparent',
                                            color: activeFilter === tab ? '#007bff' : '#6c757d',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            boxShadow: activeFilter === tab ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>

                            <button
                                onClick={() => {
                                    setInitialFormState(null);
                                    setIsCreating(true);
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                                    padding: '0.5rem 1rem', background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', color: 'white',
                                    border: 'none', borderRadius: '4px', cursor: 'pointer',
                                    boxShadow: '0 4px 6px rgba(0,123,255,0.2)', fontWeight: 500
                                }}
                            >
                                <Plus size={18} /> New Task
                            </button>
                        </div>
                    )}
                </div>

                {/* Sub-Filters / Chips */}
                {activeFilter !== 'All' && TASK_TYPES[activeFilter] && (
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingLeft: '0.5rem' }}>
                        <button
                            onClick={() => setActiveSubFilter(null)}
                            style={{
                                padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.85rem', cursor: 'pointer',
                                border: '1px solid #dee2e6',
                                background: !activeSubFilter ? '#e9ecef' : 'white',
                                color: !activeSubFilter ? '#495057' : '#6c757d',
                                fontWeight: !activeSubFilter ? 600 : 400
                            }}
                        >
                            All {activeFilter}s
                        </button>
                        {TASK_TYPES[activeFilter].map(subType => (
                            <button
                                key={subType}
                                onClick={() => setActiveSubFilter(subType === activeSubFilter ? null : subType)}
                                style={{
                                    padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.85rem', cursor: 'pointer',
                                    border: `1px solid ${subType === activeSubFilter ? '#007bff' : '#dee2e6'}`,
                                    background: subType === activeSubFilter ? '#e7f1ff' : 'white',
                                    color: subType === activeSubFilter ? '#0056b3' : '#6c757d',
                                    fontWeight: subType === activeSubFilter ? 600 : 400,
                                    transition: 'all 0.2s'
                                }}
                            >
                                {subType}
                            </button>
                        ))}
                    </div>
                )}
                </div>

                {/* Filter Bar for Everyone */}
                <div style={{ marginBottom: '1rem' }}>
                    <DGFilterBar onFilterChange={setFilters} />
                </div>

                {error && <div style={{ padding: '1rem', background: '#fee', color: '#c00', borderRadius: '4px', marginBottom: '1rem' }}>{error}</div>}
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingRight: '4px', paddingBottom: '2rem' }}>
                {isCreating && (
                    <div style={{ marginBottom: '2rem' }}>
                        <TaskForm onSubmit={handleCreate} onCancel={() => setIsCreating(false)} initialData={initialFormState} users={users} />
                    </div>
                )}

                {loading ? (
                <ContentLoader text={`Loading ${displayDivisionName} tasks...`} />
            ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                    {tasks.length === 0 ? (
                        <p style={{ color: '#666', fontStyle: 'italic' }}>No tasks found matching your filters.</p>
                    ) : (
                        tasks.map(task => {
                            const isExpanded = expandedTaskId === task.TaskID;
                            const toSubtaskArray = (value) => {
                                if (Array.isArray(value)) return value;
                                if (value && Array.isArray(value.items)) return value.items;
                                if (value && Array.isArray(value.data)) return value.data;
                                if (value && Array.isArray(value.Subtasks)) return value.Subtasks;
                                return [];
                            };
                            // Use state subtasks if loaded, otherwise fallback to task.Subtasks; always force array.
                            const taskSubtasks = toSubtaskArray(subtasks[task.TaskID] ?? task.Subtasks);

                            // Calculate Progress
                            let totalSub = 0;
                            let completedSub = 0;

                            if (typeof task.TotalSubTasks === 'number') {
                                totalSub = task.TotalSubTasks;
                                completedSub = task.CompletedSubTasks || 0;
                            } else {
                                const subs = toSubtaskArray(subtasks[task.TaskID] ?? task.Subtasks);
                                totalSub = subs.length;
                                completedSub = subs.filter(s => s.Status === 'Completed').length;
                            }
                            let progressPercent = 0;

                            if (task.Status === 'Completed') {
                                progressPercent = 100;
                            } else if (totalSub > 0) {
                                progressPercent = Math.round((completedSub / totalSub) * 100);
                            }

                            // Card colors are based on status/progress only (not expand state).
                            let cardBackground = 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)';
                            let borderColor = '#e9ecef';

                            if (task.Status === 'Completed') {
                                cardBackground = '#d1e7dd';
                                borderColor = '#badbcc';
                            } else if (task.Status === 'Cancelled') {
                                cardBackground = '#f8d7da';
                                borderColor = '#f5c6cb';
                            } else if (totalSub > 0 && completedSub === totalSub) {
                                cardBackground = '#fff3cd';
                                borderColor = '#ffecb5';
                                if (task.Status !== 'Completed') {
                                    // Force display as In Progress if not officially completed yet, so dropdown reads In Progress
                                    task = { ...task, Status: 'In Progress' };
                                }
                            } else if (task.Status === 'Pending') {
                                cardBackground = 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)';
                                borderColor = '#a5b4fc';
                            } else if (progressPercent > 0 || task.Status === 'In Progress') {
                                cardBackground = 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)';
                                borderColor = '#93c5fd';
                            }

                            return (
                                <div key={task.TaskID} className="modern-card" style={{
                                    padding: 0, overflow: 'visible',
                                    border: `1px solid ${borderColor}`,
                                    borderLeft: isExpanded ? '4px solid #007bff' : `1px solid ${borderColor}`, // Extra accent on expand
                                    boxShadow: isExpanded ? '0 8px 16px rgba(0,123,255,0.15)' : '0 2px 4px rgba(0,0,0,0.05)',
                                    marginBottom: '1rem', borderRadius: '12px',
                                    background: cardBackground,
                                    transition: 'all 0.3s ease'
                                }}>
                                    <div
                                        onClick={() => toggleExpand(task.TaskID)}
                                        style={{
                                            padding: '1.25rem', display: 'flex', alignItems: 'center', gap: '1rem',
                                            cursor: 'pointer',
                                            background: 'transparent', // Let parent gradient show through
                                            borderBottom: isExpanded ? '1px solid #90caf9' : 'none',
                                            transition: 'background 0.2s'
                                        }}
                                    >
                                        {/* 1. Icon (Fixed) */}
                                        <div style={{ flexShrink: 0 }}>
                                            {isExpanded ? <ChevronDown size={20} color="#666" /> : <ChevronRight size={20} color="#666" />}
                                        </div>

                                        {/* 2. Title & Details (Flexible) */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h4 style={{
                                                margin: '0 0 0.25rem 0',
                                                wordWrap: 'break-word',
                                                whiteSpace: 'normal',
                                                textDecoration: task.Status === 'Cancelled' ? 'line-through' : 'none',
                                                textDecorationColor: 'red'
                                            }}>{task.Title}</h4>
                                            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                                                {task.TaskType} • {
                                                    task.Status === 'Completed' ? <span style={{ color: '#28a745' }}>Completed on {formatDate(task.UpdatedAt)}</span> :
                                                        task.Status === 'Cancelled' ? <span style={{ color: '#dc3545' }}>Cancelled on {formatDate(task.UpdatedAt)}</span> :
                                                            formatDueDate(task.EndDate || task.DueDate)
                                                }
                                            </p>
                                            <p style={{ margin: '0.2rem 0 0 0', color: '#888', fontSize: '0.8rem' }}>
                                                Created by: {getUserName(task.CreatedBy, users)}

                                                {/* Assigned To Display */}
                                                {task.AssigneeID && (
                                                    <span style={{ marginLeft: '1rem' }}>
                                                        Assigned to: <strong>
                                                            {task.TransferStatus === 'PendingTransfer'
                                                                ? getUserName(task.PendingTargetAssigneeId, users)
                                                                : getUserName(task.AssigneeID, users)}
                                                        </strong>
                                                        {task.TransferStatus === 'PendingTransfer' && <span style={{ color: '#b45309', marginLeft: '0.5rem', fontStyle: 'italic' }}>(Pending {task.AssigneeDivision} Coordinator Approval)</span>}
                                                        {!task.TransferStatus && task.AssigneeDivision && <span style={{ color: '#666', fontWeight: 'normal' }}> ({task.AssigneeDivision})</span>}
                                                    </span>
                                                )}

                                                {/* DG View: Show Division if not assigned to anyone specific or if just Division is relevant */}
                                                {(isDG || isCoordinator) && task.Division && !task.AssigneeDivision && (
                                                    <span style={{ marginLeft: '1rem' }}>
                                                        Division: <strong>{task.Division}</strong>
                                                    </span>
                                                )}
                                            </p>

                                            {(() => {
                                                const myDivision = (user?.attributes?.division || user?.roles?.find(r => r.startsWith('COORDINATOR_'))?.split('_').slice(1).join('_') || '').toLowerCase().trim();
                                                const isTargetDivision = task.AssigneeDivision && myDivision === task.AssigneeDivision.toLowerCase().trim();
                                                const hasTargetGroup = user?.roles?.some(g => g.toUpperCase() === `COORDINATOR_${(task.AssigneeDivision || '').toUpperCase()}`);
                                                const isTargetCoordinator = task.TransferStatus === 'PendingTransfer' && (isTargetDivision || hasTargetGroup);

                                                if (isTargetCoordinator) {
                                                    return (
                                                        <div style={{ marginTop: '0.75rem', marginBottom: '0.25rem', padding: '0.75rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '0.85rem' }} onClick={e => e.stopPropagation()}>
                                                            <strong style={{ color: '#b45309' }}>Resource Request:</strong> Another division wants to assign this task to your employee, <strong>{getUserName(task.PendingTargetAssigneeId, users)}</strong>.
                                                            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleTransferAction(task.TaskID, 'APPROVE_TRANSFER'); }}
                                                                    style={{ background: '#10b981', color: 'white', border: 'none', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>Approve</button>
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); handleTransferAction(task.TaskID, 'REJECT_TRANSFER'); }}
                                                                    style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.25rem 0.75rem', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>Reject</button>
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            <p style={{ margin: '0.2rem 0 0 0', color: '#666', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                                                    <Paperclip size={13} />
                                                    {(Array.isArray(task.Attachments) ? task.Attachments.length : 0)} file(s) attached
                                                </span>

                                                {/* Parent Task Comment UI */}
                                                <span
                                                    style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                                                    onClick={(e) => { e.stopPropagation(); setActiveTaskCommentModal(activeTaskCommentModal === task.TaskID ? null : task.TaskID); }}
                                                >
                                                    <MessageCircle size={13} color={(task.Comments && task.Comments.length > 0) ? '#007bff' : '#adb5bd'} />
                                                    {(task.Comments && task.Comments.length > 0) ? `${task.Comments.length} comment(s)` : 'Add comment'}

                                                    {activeTaskCommentModal === task.TaskID && (
                                                        <div style={{
                                                            position: 'absolute', top: '20px', left: '0',
                                                            width: '280px', background: 'white', border: '1px solid #ccc',
                                                            borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            zIndex: 1000, padding: '0.75rem', cursor: 'default'
                                                        }}
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#333' }}>Task Comments</h6>
                                                            {task.Comments && task.Comments.length > 0 ? (
                                                                <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                                    {task.Comments.map((c, i) => (
                                                                        <div key={i} style={{ fontSize: '0.8rem', background: '#f8f9fa', padding: '0.4rem', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                                                                                <strong style={{ color: '#495057' }}>{c.AddedByName || 'Unknown'}</strong>
                                                                            </div>
                                                                            <div style={{ color: '#333', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                                                                                {c.Text}
                                                                            </div>
                                                                            <div style={{ fontSize: '0.65rem', color: '#adb5bd', marginTop: '0.3rem', textAlign: 'right' }}>
                                                                                {formatDateTimeIST(c.Timestamp)}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.8rem', color: '#6c757d', fontStyle: 'italic' }}>No comments yet.</p>
                                                            )}

                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                                                <textarea
                                                                    placeholder="Add a text comment (Max 20 words)..."
                                                                    value={taskComments[task.TaskID] || ''}
                                                                    onChange={e => setTaskComments({ ...taskComments, [task.TaskID]: e.target.value })}
                                                                    style={{
                                                                        width: '100%', padding: '0.4rem', border: '1px solid #ced4da', borderRadius: '4px',
                                                                        fontSize: '0.8rem', resize: 'none', height: '50px'
                                                                    }}
                                                                />
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '0.7rem', color: ((taskComments[task.TaskID] || '').trim().split(/\s+/).length > 20) ? '#dc3545' : '#6c757d' }}>
                                                                        {((taskComments[task.TaskID] || '').trim() ? (taskComments[task.TaskID] || '').trim().split(/\s+/).length : 0)}/20
                                                                    </span>
                                                                    <button
                                                                        disabled={!taskComments[task.TaskID] || taskComments[task.TaskID].trim().length === 0 || (taskComments[task.TaskID].trim().split(/\s+/).length > 20)}
                                                                        onClick={(e) => { e.stopPropagation(); handleAddParentTaskComment(task.TaskID, task.Status); }}
                                                                        style={{
                                                                            padding: '0.2rem 0.5rem', fontSize: '0.75rem',
                                                                            background: '#007bff', color: 'white', border: 'none', borderRadius: '4px',
                                                                            cursor: (!taskComments[task.TaskID] || taskComments[task.TaskID].trim().length === 0 || (taskComments[task.TaskID].trim().split(/\s+/).length > 20)) ? 'not-allowed' : 'pointer',
                                                                            opacity: (!taskComments[task.TaskID] || taskComments[task.TaskID].trim().length === 0 || (taskComments[task.TaskID].trim().split(/\s+/).length > 20)) ? 0.6 : 1
                                                                        }}
                                                                    >
                                                                        Add
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </span>
                                            </p>
                                        </div>

                                        {/* 3. Circular Progress Ring (Fixed) */}
                                        <div style={{ flexShrink: 0, position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="40" height="40" viewBox="0 0 40 40">
                                                <circle cx="20" cy="20" r="16" fill="none" stroke="#e9ecef" strokeWidth="4" />
                                                <circle
                                                    cx="20" cy="20" r="16" fill="none" stroke={progressPercent === 100 ? '#28a745' : '#007bff'} strokeWidth="4"
                                                    strokeDasharray="100.53"
                                                    strokeDashoffset={100.53 - (progressPercent / 100) * 100.53}
                                                    transform="rotate(-90 20 20)"
                                                    strokeLinecap="round"
                                                    style={{ transition: 'stroke-dashoffset 0.5s ease' }}
                                                />
                                            </svg>
                                            <span style={{ position: 'absolute', fontSize: '0.65rem', fontWeight: 'bold', color: progressPercent === 100 ? '#28a745' : '#007bff' }}>
                                                {progressPercent}%
                                            </span>
                                        </div>

                                        <div style={{ flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                                            <input
                                                type="file"
                                                multiple
                                                ref={(el) => { fileInputRefs.current[task.TaskID] = el; }}
                                                onChange={(e) => handleTaskAttachmentSelection(task.TaskID, e)}
                                                style={{ display: 'none' }}
                                            />
                                            <select
                                                value={uploadCategoryByTask[task.TaskID] || 'PUBLICATION'}
                                                onChange={(e) => setUploadCategoryByTask(prev => ({ ...prev, [task.TaskID]: e.target.value }))}
                                                style={{
                                                    border: '1px solid #cbd5e1',
                                                    background: '#fff',
                                                    color: '#1f2937',
                                                    borderRadius: '8px',
                                                    padding: '0.3rem 0.4rem',
                                                    cursor: 'pointer',
                                                    fontSize: '0.75rem',
                                                    marginBottom: '0.3rem',
                                                    width: '170px'
                                                }}
                                            >
                                                {TASK_ATTACHMENT_CATEGORIES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                            <button
                                                onClick={() => handleTaskAttachmentPicker(task.TaskID)}
                                                style={{
                                                    border: '1px solid #ced4da',
                                                    background: '#fff',
                                                    color: '#334155',
                                                    borderRadius: '8px',
                                                    padding: '0.35rem 0.55rem',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    gap: '0.35rem',
                                                    alignItems: 'center',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 600
                                                }}
                                                title="Upload files"
                                            >
                                                <Upload size={14} />
                                                Upload
                                            </button>
                                            {uploadingByTask[task.TaskID]?.active && (
                                                <div style={{ marginTop: '0.25rem', fontSize: '0.7rem', color: '#0f766e', textAlign: 'center' }}>
                                                    Uploading {uploadingByTask[task.TaskID].done}/{uploadingByTask[task.TaskID].total}
                                                </div>
                                            )}
                                        </div>

                                        {/* 4. Status Dropdown (Fixed) */}
                                        <div style={{ flexShrink: 0, textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                            <select
                                                value={task.Status}
                                                disabled={isEmployee} // Freeze for Employees
                                                onChange={(e) => handleUpdateTaskStatus(task.TaskID, e.target.value, e)}
                                                style={{
                                                    padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem',
                                                    background: task.Status === 'Completed' ? '#d4edda' : task.Status === 'New' ? '#e3f2fd' : task.Status === 'Pending' ? '#e0e7ff' : '#fff3cd', // Indigo for Pending
                                                    color: task.Status === 'Completed' ? '#155724' : task.Status === 'New' ? '#0d47a1' : task.Status === 'Pending' ? '#4338ca' : '#856404',
                                                    border: '1px solid transparent', cursor: !isEmployee ? 'pointer' : 'not-allowed',
                                                    opacity: !isEmployee ? 1 : 0.6
                                                }}
                                            >
                                                <option value="New">New</option>
                                                <option value="Pending">Pending</option>
                                                <option value="In Progress">In Progress</option>
                                                {(isDG || isCoordinator) && (
                                                    <option value="Completed">Completed</option>
                                                )}
                                                {!(isDG || isCoordinator) && task.Status === 'Completed' && (
                                                    <option value="Completed">Completed</option>
                                                )}
                                                <option value="Cancelled">Cancelled</option>
                                            </select>
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div style={{ padding: '1rem', borderTop: '1px solid #eee', background: '#fafafa' }}>
                                            <div style={{ marginBottom: '1rem', padding: '0.75rem', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff' }}>
                                                <h5 style={{ margin: '0 0 0.5rem 0', color: '#334155' }}>Attachments</h5>
                                                {(Array.isArray(task.Attachments) && task.Attachments.length > 0) ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                        {TASK_ATTACHMENT_CATEGORIES.map((cat) => {
                                                            const inCategory = task.Attachments.filter(a => (a.Category || 'PUBLICATION') === cat.value);
                                                            if (inCategory.length === 0) return null;
                                                            return (
                                                                <div key={cat.value}>
                                                                    <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#475569', marginBottom: '0.35rem', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                                                        {cat.label}
                                                                    </div>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem', maxHeight: '156px', overflowY: 'auto', paddingRight: '0.2rem' }}>
                                                                        {inCategory.map((attachment) => (
                                                                            <div key={attachment.AttachmentID} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '0.5rem', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.45rem 0.6rem' }}>
                                                                                <div style={{ minWidth: 0 }}>
                                                                                    <div style={{ fontSize: '0.85rem', color: '#1f2937', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                        {attachment.FileName}
                                                                                    </div>
                                                                                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                                                        {(attachment.ContentType || 'file')} {attachment.SizeBytes ? `• ${(attachment.SizeBytes / 1024 / 1024).toFixed(2)} MB` : ''}
                                                                                    </div>
                                                                                </div>
                                                                                <div style={{ display: 'flex', gap: '0.35rem' }}>
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); openTaskAttachment(task.TaskID, attachment.AttachmentID, false); }}
                                                                                        style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: '6px', padding: '0.25rem 0.45rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
                                                                                    >
                                                                                        <Eye size={13} /> Preview
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); openTaskAttachment(task.TaskID, attachment.AttachmentID, true); }}
                                                                                        style={{ border: '1px solid #cbd5e1', background: '#fff', borderRadius: '6px', padding: '0.25rem 0.45rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
                                                                                    >
                                                                                        <Download size={13} /> Download
                                                                                    </button>
                                                                                    <button
                                                                                        onClick={(e) => { e.stopPropagation(); deleteTaskAttachment(task.TaskID, attachment.AttachmentID); }}
                                                                                        style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#b91c1c', borderRadius: '6px', padding: '0.25rem 0.45rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem' }}
                                                                                        title="Delete attachment"
                                                                                    >
                                                                                        <Trash2 size={13} />
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p style={{ margin: 0, fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>No files attached yet.</p>
                                                )}
                                            </div>
                                            <h5 style={{ margin: '0 0 1rem 0', color: '#555' }}>Subtasks</h5>

                                            {taskSubtasks.length === 0 && <p style={{ fontSize: '0.9rem', color: '#999', fontStyle: 'italic' }}>No subtasks yet.</p>}

                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                                                {taskSubtasks.map((sub, index) => {
                                                    let statusIcon = <Circle size={16} color="#adb5bd" fill="white" />;

                                                    if (sub.Status === 'Completed') {
                                                        statusIcon = <ShieldCheck size={18} color="#28a745" fill="#e6fffa" />;
                                                    } else if (sub.Status === 'Cancelled') {
                                                        statusIcon = <X size={18} color="#dc3545" />;
                                                    } else if (sub.Status === 'Review') {
                                                        statusIcon = <Circle size={18} color="#17a2b8" fill="#e0f7fa" />;
                                                    }

                                                    // Status Options based on Role
                                                    const myId = user?.sub || user?.Username || user?.UserId || user?.id || '';
                                                    const myEmail = user?.Email || user?.Attributes?.find(a => a.Name === 'email')?.Value || '';
                                                    const myName = user?.Name || user?.name || user?.Attributes?.find(a => a.Name === 'name')?.Value || '';
                                                    const isSubtaskAssignee = (sub.AssigneeID && (sub.AssigneeID === myId || sub.AssigneeID === myEmail || sub.AssigneeID === myName)) ||
                                                        (sub.AssigneeName && sub.AssigneeName === myName);
                                                    const canEditSubtask = isDG || isCoordinator || isSubtaskAssignee;

                                                    // Status Options based on Role
                                                    const allowedStatuses = isEmployee
                                                        ? ['Review', 'Completed']
                                                        : ['Pending', 'In Progress', 'Review', 'Completed', 'Cancelled'];

                                                    return (
                                                        <div key={sub.SubTaskID} style={{ display: 'flex', flexDirection: 'column', background: 'white', borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 1px 2px rgba(0,0,0,0.03)', marginBottom: '0.75rem' }}>
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: '30px 1fr 180px 120px 100px',
                                                                alignItems: 'center',
                                                                padding: '0.75rem',
                                                            }}>
                                                                {/* Icon */}
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    {statusIcon}
                                                                </div>

                                                                {/* Activity Name */}
                                                                <div style={{
                                                                    fontWeight: 600,
                                                                    color: '#495057',
                                                                    textDecoration: sub.Status === 'Cancelled' ? 'line-through' : 'none',
                                                                    textDecorationColor: 'red'
                                                                }}>
                                                                    {sub.ActivityNameManual || sub.ActivityType}
                                                                </div>

                                                                {/* Assignee */}
                                                                <div style={{ fontSize: '0.85rem', color: '#666', display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                                                                    <div
                                                                        style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                                                                        onClick={(e) => { e.stopPropagation(); setActiveCommentModal(activeCommentModal === sub.SubTaskID ? null : sub.SubTaskID); }}
                                                                    >
                                                                        <MessageCircle size={16} color={sub.Comments && sub.Comments.length > 0 ? '#007bff' : '#adb5bd'} />
                                                                        {sub.Comments && sub.Comments.length > 0 && (
                                                                            <span style={{ position: 'absolute', top: '-5px', right: '-8px', background: '#dc3545', color: 'white', borderRadius: '50%', padding: '0.1rem 0.3rem', fontSize: '0.6rem', fontWeight: 'bold' }}>
                                                                                {sub.Comments.length}
                                                                            </span>
                                                                        )}
                                                                        {activeCommentModal === sub.SubTaskID && (
                                                                            <div style={{
                                                                                position: 'absolute', top: '100%', left: '0', marginTop: '0.5rem',
                                                                                width: '280px', background: 'white', border: '1px solid #ccc',
                                                                                borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                                                zIndex: 1000, padding: '0.75rem', cursor: 'default'
                                                                            }}
                                                                                onClick={e => e.stopPropagation()}
                                                                            >
                                                                                <h6 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#333' }}>Comments</h6>
                                                                                {sub.Comments && sub.Comments.length > 0 ? (
                                                                                    <div style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                                                        {sub.Comments.map((c, i) => (
                                                                                            <div key={i} style={{ fontSize: '0.8rem', background: '#f8f9fa', padding: '0.4rem', borderRadius: '4px', border: '1px solid #e9ecef' }}>
                                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
                                                                                                    <strong style={{ color: '#495057' }}>{c.AddedByName || 'Unknown'}</strong>
                                                                                                </div>
                                                                                                <div style={{ color: '#333', whiteSpace: 'pre-wrap' }}>{c.Text}</div>
                                                                                            </div>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div style={{ fontSize: '0.8rem', color: '#999', fontStyle: 'italic', marginBottom: '0.5rem' }}>No previous comments.</div>
                                                                                )}
                                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                                                    <textarea
                                                                                        value={subtaskComments[sub.SubTaskID] || ''}
                                                                                        onChange={e => setSubtaskComments({ ...subtaskComments, [sub.SubTaskID]: e.target.value })}
                                                                                        placeholder="Add a comment (max 20 words)..."
                                                                                        rows={2}
                                                                                        style={{ padding: '0.4rem', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '0.8rem', width: '100%', resize: 'vertical' }}
                                                                                    />
                                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                                        <span style={{ fontSize: '0.7rem', color: ((subtaskComments[sub.SubTaskID] || '').trim() ? (subtaskComments[sub.SubTaskID] || '').trim().split(/\s+/).length : 0) > 20 ? '#dc3545' : '#6c757d' }}>
                                                                                            {((subtaskComments[sub.SubTaskID] || '').trim() ? (subtaskComments[sub.SubTaskID] || '').trim().split(/\s+/).length : 0)}/20
                                                                                        </span>
                                                                                        <button
                                                                                            disabled={!subtaskComments[sub.SubTaskID] || subtaskComments[sub.SubTaskID].trim().length === 0 || (subtaskComments[sub.SubTaskID].trim().split(/\s+/).length > 20)}
                                                                                            onClick={(e) => { e.stopPropagation(); handleAddSubtaskComment(task.TaskID, sub.SubTaskID, sub.Status); }}
                                                                                            style={{ padding: '0.2rem 0.5rem', fontSize: '0.75rem', background: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: (!subtaskComments[sub.SubTaskID] || subtaskComments[sub.SubTaskID].trim().length === 0 || (subtaskComments[sub.SubTaskID].trim().split(/\s+/).length > 20)) ? 'not-allowed' : 'pointer', opacity: (!subtaskComments[sub.SubTaskID] || subtaskComments[sub.SubTaskID].trim().length === 0 || (subtaskComments[sub.SubTaskID].trim().split(/\s+/).length > 20)) ? 0.6 : 1 }}
                                                                                        >
                                                                                            Send
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {sub.AssigneeName ? sub.AssigneeName : <span style={{ color: '#999' }}>Unassigned</span>}
                                                                </div>

                                                                {/* Date */}
                                                                <div style={{ fontSize: '0.85rem', color: '#868e96', textAlign: 'right', paddingRight: '1rem' }}>
                                                                    {sub.CompletionDate ? new Date(sub.CompletionDate).toLocaleDateString() : 'No Date'}
                                                                </div>

                                                                {/* Status Action */}
                                                                <div style={{ position: 'relative' }}>
                                                                    <button
                                                                        disabled={!canEditSubtask}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setSubtaskStatusMenuOpen(subtaskStatusMenuOpen === sub.SubTaskID ? null : sub.SubTaskID);
                                                                        }}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '0.3rem 0.5rem',
                                                                            background: 'white',
                                                                            border: '1px solid #ddd',
                                                                            borderRadius: '4px',
                                                                            fontSize: '0.8rem',
                                                                            color: '#333',
                                                                            cursor: canEditSubtask ? 'pointer' : 'not-allowed',
                                                                            opacity: canEditSubtask ? 1 : 0.6,
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between'
                                                                        }}
                                                                    >
                                                                        {sub.Status || 'Pending'} <ChevronDown size={14} />
                                                                    </button>

                                                                    {subtaskStatusMenuOpen === sub.SubTaskID && (
                                                                        <div style={{
                                                                            position: 'absolute', top: '100%', right: 0,
                                                                            marginTop: '0.25rem', background: 'white', border: '1px solid #ccc',
                                                                            borderRadius: '4px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                                            zIndex: 100, minWidth: '120px', padding: '0.5rem'
                                                                        }}>
                                                                            {allowedStatuses.map(status => (
                                                                                <div
                                                                                    key={status}
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleSubtaskStatus(task.TaskID, sub.SubTaskID, status);
                                                                                        setSubtaskStatusMenuOpen(null);
                                                                                    }}
                                                                                    style={{
                                                                                        fontSize: '0.8rem', padding: '0.4rem', cursor: 'pointer',
                                                                                        color: status === 'Completed' ? '#28a745' : status === 'Cancelled' ? '#dc3545' : '#17a2b8',
                                                                                        transition: 'background 0.1s'
                                                                                    }}
                                                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                                                                                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                                                                                >
                                                                                    {status}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            {
                                                creatingSubtaskFor === task.TaskID ? (
                                                    <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #ddd' }}>
                                                        <h6 style={{ marginTop: 0 }}>New Subtask</h6>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                                            <input
                                                                placeholder="Activity Name"
                                                                value={newSubtaskData.activityNameManual}
                                                                onChange={e => setNewSubtaskData({ ...newSubtaskData, activityNameManual: e.target.value })}
                                                                style={{ padding: '0.4rem' }}
                                                            />
                                                            <input
                                                                placeholder="Activity Type (optional)"
                                                                value={newSubtaskData.activityType}
                                                                onChange={e => setNewSubtaskData({ ...newSubtaskData, activityType: e.target.value })}
                                                                style={{ padding: '0.4rem' }}
                                                            />

                                                            {/* Skills Input */}
                                                            <div style={{ gridColumn: '1 / -1', position: 'relative' }}>
                                                                <div style={{
                                                                    border: '1px solid #ced4da', borderRadius: '6px', padding: '0.4rem',
                                                                    background: 'white', minHeight: '38px', display: 'flex', flexWrap: 'wrap', gap: '0.25rem'
                                                                }}>
                                                                    {newSubtaskData.skills && newSubtaskData.skills.map(skill => (
                                                                        <span key={skill} style={{
                                                                            background: '#e3f2fd', color: '#0d47a1', fontSize: '0.8rem',
                                                                            padding: '2px 8px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '4px'
                                                                        }}>
                                                                            {skill}
                                                                            <button type="button" onClick={() => {
                                                                                const newSkills = newSubtaskData.skills.filter(s => s !== skill);
                                                                                setNewSubtaskData(prev => ({ ...prev, skills: newSkills }));
                                                                            }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#0d47a1', padding: 0, fontSize: '1rem', lineHeight: 1 }}>&times;</button>
                                                                        </span>
                                                                    ))}
                                                                    <input
                                                                        placeholder={newSubtaskData.skills?.length > 0 ? "" : "Required Skills..."}
                                                                        list="subtask-skill-suggestions"
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter') {
                                                                                e.preventDefault();
                                                                                const val = e.target.value.trim();
                                                                                if (val && (!newSubtaskData.skills || !newSubtaskData.skills.includes(val))) {
                                                                                    setNewSubtaskData(prev => ({ ...prev, skills: [...(prev.skills || []), val] }));
                                                                                    e.target.value = '';
                                                                                }
                                                                            }
                                                                        }}
                                                                        style={{ border: 'none', outline: 'none', flex: 1, minWidth: '120px', fontSize: '0.9rem' }}
                                                                    />
                                                                    <datalist id="subtask-skill-suggestions">
                                                                        {Array.from(new Set(users.flatMap(u => u.Tags || []))).sort().map(tag => (
                                                                            <option key={tag} value={tag} />
                                                                        ))}
                                                                    </datalist>
                                                                </div>
                                                            </div>

                                                            <input
                                                                type="date"
                                                                value={newSubtaskData.completionDate}
                                                                onChange={e => setNewSubtaskData({ ...newSubtaskData, completionDate: e.target.value })}
                                                                style={{ padding: '0.4rem' }}
                                                            />
                                                            <UserSearchSelect
                                                                users={users}
                                                                value={newSubtaskData.assigneeId}
                                                                onChange={(id, selectedUser) => {
                                                                    const userName = selectedUser ? (selectedUser.Name || selectedUser.name || selectedUser.Username || '') : '';
                                                                    setNewSubtaskData({ ...newSubtaskData, assigneeId: id, assigneeName: userName });
                                                                }}
                                                                placeholder="Search assignee…"
                                                                isEmployee={isEmployee}
                                                                currentUser={user}
                                                                requiredSkills={newSubtaskData.skills || []}
                                                                compact
                                                            />
                                                        </div>
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            <button onClick={() => handleCreateSubtask(task.TaskID)} style={{ padding: '0.3rem 0.8rem', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                                                            <button onClick={() => setCreatingSubtaskFor(null)} style={{ padding: '0.3rem 0.8rem', background: '#ccc', color: 'black', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleOpenSubtaskForm(task.TaskID)}
                                                        style={{ fontSize: '0.85rem', color: '#007bff', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                                    >
                                                        <Plus size={14} /> Add Subtask
                                                    </button>
                                                )
                                            }
                                        </div>
                                    )
                                    }
                                </div>
                            );
                        })
                    )}
                </div>
            )}
            </div>
        </div>
    );
};

export default Tasks;
